import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';
import { formatDZ } from '../../../utils/currency';

const CustomerAnalytics = () => {
  const customerSegmentData = [
    { name: 'New Customers', value: 35, color: '#0066ff' },
    { name: 'Returning Customers', value: 45, color: '#10b981' },
    { name: 'VIP Customers', value: 15, color: '#f59e0b' },
    { name: 'Inactive Customers', value: 5, color: '#ef4444' }
  ];

  const customerActivityData = [
    { month: 'Jan', newCustomers: 234, returningCustomers: 456, totalOrders: 690 },
    { month: 'Feb', newCustomers: 287, returningCustomers: 523, totalOrders: 810 },
    { month: 'Mar', newCustomers: 256, returningCustomers: 489, totalOrders: 745 },
    { month: 'Apr', newCustomers: 342, returningCustomers: 612, totalOrders: 954 },
    { month: 'May', newCustomers: 298, returningCustomers: 567, totalOrders: 865 },
    { month: 'Jun', newCustomers: 378, returningCustomers: 634, totalOrders: 1012 },
    { month: 'Jul', newCustomers: 412, returningCustomers: 698, totalOrders: 1110 },
    { month: 'Aug', newCustomers: 389, returningCustomers: 723, totalOrders: 1112 },
    { month: 'Sep', newCustomers: 445, returningCustomers: 756, totalOrders: 1201 }
  ];

  const topCustomers = [
    {
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      totalSpent: 4299.97,
      orders: 12,
      lastOrder: '2024-09-23',
      status: 'VIP',
      avatar: 'https://randomuser.me/api/portraits/women/32.jpg'
    },
    {
      id: 2,
      name: 'Michael Chen',
      email: 'm.chen@email.com',
      totalSpent: 3899.95,
      orders: 8,
      lastOrder: '2024-09-22',
      status: 'VIP',
      avatar: 'https://randomuser.me/api/portraits/men/45.jpg'
    },
    {
      id: 3,
      name: 'Emily Davis',
      email: 'emily.davis@email.com',
      totalSpent: 2799.96,
      orders: 6,
      lastOrder: '2024-09-21',
      status: 'Premium',
      avatar: 'https://randomuser.me/api/portraits/women/28.jpg'
    },
    {
      id: 4,
      name: 'David Wilson',
      email: 'd.wilson@email.com',
      totalSpent: 2199.98,
      orders: 5,
      lastOrder: '2024-09-20',
      status: 'Premium',
      avatar: 'https://randomuser.me/api/portraits/men/52.jpg'
    },
    {
      id: 5,
      name: 'Lisa Anderson',
      email: 'lisa.a@email.com',
      totalSpent: 1899.99,
      orders: 4,
      lastOrder: '2024-09-19',
      status: 'Regular',
      avatar: 'https://randomuser.me/api/portraits/women/41.jpg'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      VIP: 'bg-warning/20 text-warning border-warning/30',
      Premium: 'bg-primary/20 text-primary border-primary/30',
      Regular: 'bg-success/20 text-success border-success/30'
    };
    return colors?.[status] || colors?.Regular;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {`${entry?.dataKey}: ${entry?.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Customer Segment Overview */}
      <div className="holographic-card rounded-xl p-6">
        <h3 className="text-xl font-bold text-foreground mb-6">Customer Segments</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={customerSegmentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {customerSegmentData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-foreground)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Legend & Stats */}
          <div className="space-y-4">
            {customerSegmentData?.map((segment, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/10 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: segment?.color }}
                  />
                  <span className="text-sm font-medium text-foreground">{segment?.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-foreground">{segment?.value}%</span>
                  <p className="text-xs text-muted-foreground">
                    {Math.round((segment?.value / 100) * 1250)} customers
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Customer Activity Trends */}
      <div className="holographic-card rounded-xl p-6">
        <h3 className="text-xl font-bold text-foreground mb-6">Customer Activity Trends</h3>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={customerActivityData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="newCustomers" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
              <Bar dataKey="returningCustomers" fill="var(--color-success)" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="flex items-center justify-center space-x-6 mt-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary rounded-full" />
            <span className="text-sm text-muted-foreground">New Customers</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full" />
            <span className="text-sm text-muted-foreground">Returning Customers</span>
          </div>
        </div>
      </div>
      {/* Top Customers */}
      <div className="holographic-card rounded-xl overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-foreground">Top Customers</h3>
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={20} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">1,250 total customers</span>
            </div>
          </div>
        </div>
        
        <div className="divide-y divide-border">
          {topCustomers?.map((customer, index) => (
            <div key={customer?.id} className="p-6 hover:bg-muted/10 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <img
                      src={customer?.avatar}
                      alt={customer?.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-white">#{index + 1}</span>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-foreground">{customer?.name}</h4>
                    <p className="text-sm text-muted-foreground">{customer?.email}</p>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {customer?.orders} orders
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Last order: {customer?.lastOrder}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="flex items-center space-x-3">
                    <div>
                      <p className="text-lg font-bold text-foreground">
                        {formatDZ(customer?.totalSpent)}
                      </p>
                      <p className="text-sm text-muted-foreground">Total Spent</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(customer?.status)}`}>
                      {customer?.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="p-6 border-t border-border bg-muted/10">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Showing top 5 customers by total spending
            </span>
            <div className="flex items-center space-x-2">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span className="text-sm text-success font-medium">+12% customer retention this month</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAnalytics;